import { useState, useEffect } from 'react';
import { db } from '../services/firebaseService';
import { collection, query, onSnapshot, orderBy, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { AppNotification } from '../types';

export const useNotifications = () => {
    const { userProfile, currentUser } = useAuth();
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const tenantId = userProfile?.tenantId;
    const userId = currentUser?.uid;

    useEffect(() => {
        if (!tenantId || !userId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        const notifRef = collection(db, `tenants/${tenantId}/users/${userId}/notifications`);
        // We only want the recent ones, maybe order by createdAt desc
        const q = query(notifRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetched: AppNotification[] = [];
            let unread = 0;
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                fetched.push({ id: docSnap.id, ...data } as AppNotification);
                if (!data.read) unread++;
            });
            setNotifications(fetched);
            setUnreadCount(unread);
        }, (err) => {
            console.error("Error fetching notifications:", err);
        });

        return () => unsubscribe();
    }, [tenantId, userId]);

    const markAsRead = async (notifId: string) => {
        if (!tenantId || !userId) return;
        try {
            const docRef = doc(db, `tenants/${tenantId}/users/${userId}/notifications/${notifId}`);
            await updateDoc(docRef, { read: true });
        } catch (error) {
            console.error("Failed to mark notification as read", error);
        }
    };

    const markAllAsRead = async () => {
        if (!tenantId || !userId) return;
        const unreadNotifs = notifications.filter(n => !n.read);
        for (const notif of unreadNotifs) {
            await markAsRead(notif.id);
        }
    };

    return {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead
    };
};
